import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import Cookies from 'js-cookie';
import { useCookies } from 'react-cookie';
import './Groups.css'; // Import the CSS file for styling

function AddGroup() {
    const [groupType, setGroupType] = useState('core'); // New: Group type selection
    const [group, setGroup] = useState({ 
        name: '', 
        grade_level: '', 
        teacher: '', // For CTE and homeroom groups only
        core_class: '', // Optional for core groups
        capacity: '', // For homeroom
        room_number: '' // For homeroom
    });
    const [gradeLevels, setGradeLevels] = useState([]);
    const [teachers, setTeachers] = useState([]);
    const [coreClasses, setCoreClasses] = useState([]); // For core class subjects
    const [loading, setLoading] = useState(true);
    const [cookies] = useCookies(['csrftoken']);

    // Group type options
    const groupTypeOptions = [
        { value: 'core', label: 'Core Class Group' },
        { value: 'cte', label: 'CTE Group' },
        { value: 'homeroom', label: 'Homeroom Group' }
    ];

    useEffect(() => {
        // Fetch grade levels
        axios.get('/courses/api/grade_levels/')
            .then(response => {
                if (Array.isArray(response.data)) {
                    setGradeLevels(response.data);
                } else {
                    console.error('Unexpected response data format:', response.data);
                }
            })
            .catch(error => console.error('Error fetching grade levels:', error));

        // Fetch core classes for core group type (optional)
        if (groupType === 'core') {
            axios.get('/api/core-classes/')
                .then(response => {
                    setCoreClasses(response.data);
                })
                .catch(error => console.error('Error fetching core classes:', error));
        }

        // Fetch teachers only for CTE and homeroom groups (core groups don't need teachers at creation)
        if (groupType === 'cte' || groupType === 'homeroom') {
            let teacherEndpoint = '/api/teachers/';
            if (groupType === 'cte') {
                teacherEndpoint = '/api/cte-teachers/';
            }

            axios.get(teacherEndpoint)
                .then(response => {
                    setTeachers(response.data);
                })
                .catch(error => console.error('Error fetching teachers:', error))
                .finally(() => setLoading(false));
        } else {
            setLoading(false);
        }
    }, [groupType]); // Re-fetch when group type changes

    const handleChange = (e) => {
        setGroup({ ...group, [e.target.name]: e.target.value });
    };

    const handleGroupTypeChange = (e) => {
        setGroupType(e.target.value);
        // Reset form when group type changes
        setGroup({ 
            name: '', 
            grade_level: '', 
            teacher: '',
            core_class: '',
            capacity: '',
            room_number: ''
        });
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        
        // Prepare data based on group type
        let groupData = { 
            name: group.name,
            grade_level: group.grade_level
        };

        // Add type-specific fields
        if (groupType === 'core') {
            // Core groups are now shared resources - no teacher required at creation
            if (group.core_class) {
                groupData.core_class = group.core_class;
            }
        } else if (groupType === 'cte') {
            groupData.cte_teacher = group.teacher;
        } else if (groupType === 'homeroom') {
            groupData.homeroom_teacher = group.teacher;
            groupData.capacity = group.capacity;
            groupData.room_number = group.room_number;
        }

        // Determine API endpoint based on group type
        let endpoint = '';
        if (groupType === 'core') {
            endpoint = '/api/groups/core-groups/add/';
        } else if (groupType === 'cte') {
            endpoint = '/api/groups/cte-groups/add/';
        } else if (groupType === 'homeroom') {
            endpoint = '/api/groups/homeroom-groups/add/';
        }

        axios.post(endpoint, groupData, {
            withCredentials: true
        })
        .then(response => {
            alert(response.data.message || `${groupTypeOptions.find(opt => opt.value === groupType)?.label} created successfully!`);
            // Reset form
            setGroup({ 
                name: '', 
                grade_level: '', 
                teacher: '',
                subject: '',
                capacity: '',
                room_number: ''
            });
        })
        .catch(error => {
            console.error('Error adding group:', error);
            if (error.response) {
                alert(`Error: ${error.response.data.message || JSON.stringify(error.response.data)}`);
            } else {
                alert('An unexpected error occurred.');
            }
        });
    };

    // Render form fields based on group type
    const renderTypeSpecificFields = () => {
        switch (groupType) {
            case 'core':
                return (
                    <>
                        <select
                            name="core_class"
                            value={group.core_class}
                            onChange={handleChange}
                            disabled={loading || coreClasses.length === 0}
                            className="group-select"
                        >
                            <option value="">Select Core Class (Optional)</option>
                            {coreClasses.map(coreClass => (
                                <option key={coreClass.id} value={coreClass.id}>
                                    {coreClass.name}
                                </option>
                            ))}
                        </select>
                        <div className="info-text">
                            <small>Core groups are shared resources available to all core teachers. Teacher assignments are handled during scheduling.</small>
                        </div>
                    </>
                );
            case 'cte':
                return (
                    <select
                        name="teacher"
                        value={group.teacher}
                        onChange={handleChange}
                        required
                        disabled={loading || teachers.length === 0}
                        className="group-select"
                    >
                        <option value="">Select CTE Teacher</option>
                        {teachers.map(teacher => (
                            <option key={teacher.id} value={teacher.id}>
                                {teacher.first_name} {teacher.last_name}
                            </option>
                        ))}
                    </select>
                );
            case 'homeroom':
                return (
                    <>
                        <select
                            name="teacher"
                            value={group.teacher}
                            onChange={handleChange}
                            required
                            disabled={loading || teachers.length === 0}
                            className="group-select"
                        >
                            <option value="">Select Homeroom Teacher</option>
                            {teachers.map(teacher => (
                                <option key={teacher.id} value={teacher.id}>
                                    {teacher.first_name} {teacher.last_name}
                                </option>
                            ))}
                        </select>
                        <input
                            type="number"
                            name="capacity"
                            value={group.capacity}
                            onChange={handleChange}
                            placeholder="Room Capacity"
                            min="1"
                            className="group-input"
                        />
                        <input
                            type="text"
                            name="room_number"
                            value={group.room_number}
                            onChange={handleChange}
                            placeholder="Room Number"
                            className="group-input"
                        />
                    </>
                );
            default:
                return null;
        }
    };

    return (
        <div className="add-group-container">
            <h2>Add New Group</h2>
            <form className="add-group-form" onSubmit={handleSubmit}>
                {/* Group Type Selection */}
                <select
                    name="groupType"
                    value={groupType}
                    onChange={handleGroupTypeChange}
                    className="group-select"
                >
                    {groupTypeOptions.map(option => (
                        <option key={option.value} value={option.value}>
                            {option.label}
                        </option>
                    ))}
                </select>

                {/* Common Fields */}
                <input
                    type="text"
                    name="name"
                    value={group.name}
                    onChange={handleChange}
                    placeholder="Group Name"
                    required
                    className="group-input"
                />
                
                <select
                    name="grade_level"
                    value={group.grade_level}
                    onChange={handleChange}
                    required
                    disabled={loading || gradeLevels.length === 0}
                    className="group-select"
                >
                    {loading || gradeLevels.length === 0 ? (
                        <option value="">
                            {loading ? 'Loading Grade Levels...' : 'No Grade Levels Available'}
                        </option>
                    ) : (
                        <option value="">Select Grade Level</option>
                    )}
                    {gradeLevels.map(grade => (
                        <option key={grade.id} value={grade.id}>{grade.grade}</option>
                    ))}
                </select>

                {/* Type-specific Fields */}
                {renderTypeSpecificFields()}

                <button type="submit" className="group-submit-btn" disabled={loading}>
                    Add {groupTypeOptions.find(opt => opt.value === groupType)?.label}
                </button>
            </form>
        </div>
    );
}

export default AddGroup;
